"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.userPublicSchema = exports.auditEvents = exports.auditOutcome = exports.auditAction = exports.rateLimit = exports.twoFactor = exports.verification = exports.account = exports.session = exports.user = exports.roleEnum = void 0;
// The ONE database schema (T08 §9.2, T04 §9.2/§9.3, T12 §9.2, T11). drizzle-kit reads this file;
// migrations in ../../drizzle are generated from it and never hand-edited (except *_custom migrations).
// Column names are derived by `casing: "snake_case"` (drizzle.config.ts), so `emailVerified` -> email_verified.
//
// Tables `user`, `session`, `account`, `verification`, `twoFactor`, `rateLimit` follow the shape
// `auth generate --config apps/server/src/plugins/auth.ts` produces for Better Auth 1.7.5 with the
// admin + twoFactor plugins and database rate limiting. Re-run `pnpm auth:generate` after changing
// the auth plugins and diff the output against this file.
const pg_core_1 = require("drizzle-orm/pg-core");
const drizzle_orm_1 = require("drizzle-orm");
const drizzle_zod_1 = require("drizzle-zod");
// ---------- enums ----------
exports.roleEnum = (0, pg_core_1.pgEnum)("role", ["admin", "clinician", "viewer"]);
// ---------- Better Auth core (T04) ----------
exports.user = (0, pg_core_1.pgTable)("user", {
    id: (0, pg_core_1.uuid)()
        .primaryKey()
        .default((0, drizzle_orm_1.sql) `gen_random_uuid()`),
    name: (0, pg_core_1.text)().notNull(),
    email: (0, pg_core_1.text)().notNull(),
    emailVerified: (0, pg_core_1.boolean)().notNull().default(false),
    image: (0, pg_core_1.text)(),
    createdAt: (0, pg_core_1.timestamp)({ withTimezone: true }).notNull().defaultNow(),
    updatedAt: (0, pg_core_1.timestamp)({ withTimezone: true }).notNull().defaultNow(),
    // admin plugin
    role: (0, pg_core_1.text)().notNull().default("viewer"),
    banned: (0, pg_core_1.boolean)().notNull().default(false),
    banReason: (0, pg_core_1.text)(),
    banExpires: (0, pg_core_1.timestamp)({ withTimezone: true }),
    // twoFactor plugin
    twoFactorEnabled: (0, pg_core_1.boolean)().notNull().default(false),
    // J-244: the account must change its password before anything else. Better Auth's additionalFields
    // (`plugins/auth.ts`) default it to true on every account Better Auth creates; the column's own
    // default is false, so the migration leaves every existing account unflagged. An admin password
    // reset sets it; a successful `/change-password` clears it.
    mustChangePassword: (0, pg_core_1.boolean)().notNull().default(false),
    // J-408: when an emailed temporary password stops signing in (set with it, cleared by a password change); null
    // for every other password.
    temporaryPasswordExpiresAt: (0, pg_core_1.timestamp)({ withTimezone: true }),
}, (t) => [(0, pg_core_1.uniqueIndex)("user_email_uidx").on(t.email), (0, pg_core_1.index)("user_role_idx").on(t.role)]);
exports.session = (0, pg_core_1.pgTable)("session", {
    id: (0, pg_core_1.uuid)()
        .primaryKey()
        .default((0, drizzle_orm_1.sql) `gen_random_uuid()`),
    // CR-4: expiresAt slides by exactly 15 min on every request (updateAge: 0); createdAt is the
    // anchor for the 12 h absolute cap enforced by requireSession.
    expiresAt: (0, pg_core_1.timestamp)({ withTimezone: true }).notNull(),
    token: (0, pg_core_1.text)().notNull(),
    createdAt: (0, pg_core_1.timestamp)({ withTimezone: true }).notNull().defaultNow(),
    updatedAt: (0, pg_core_1.timestamp)({ withTimezone: true }).notNull().defaultNow(),
    ipAddress: (0, pg_core_1.text)(),
    userAgent: (0, pg_core_1.text)(),
    userId: (0, pg_core_1.uuid)()
        .notNull()
        .references(() => exports.user.id, { onDelete: "cascade" }),
    // admin plugin
    impersonatedBy: (0, pg_core_1.text)(),
}, (t) => [(0, pg_core_1.uniqueIndex)("session_token_uidx").on(t.token), (0, pg_core_1.index)("session_user_idx").on(t.userId)]);
exports.account = (0, pg_core_1.pgTable)("account", {
    id: (0, pg_core_1.uuid)()
        .primaryKey()
        .default((0, drizzle_orm_1.sql) `gen_random_uuid()`),
    // Better Auth 1.7.3+ identifies an account by (providerId, accountId); the 1.7.0–1.7.2 `issuer`
    // column was made nullable by J-005 and dropped by J-006 once 1.7.5 ran on QA (D-1179, D-1181).
    accountId: (0, pg_core_1.text)().notNull(),
    providerId: (0, pg_core_1.text)().notNull(),
    userId: (0, pg_core_1.uuid)()
        .notNull()
        .references(() => exports.user.id, { onDelete: "cascade" }),
    accessToken: (0, pg_core_1.text)(),
    refreshToken: (0, pg_core_1.text)(),
    idToken: (0, pg_core_1.text)(),
    accessTokenExpiresAt: (0, pg_core_1.timestamp)({ withTimezone: true }),
    refreshTokenExpiresAt: (0, pg_core_1.timestamp)({ withTimezone: true }),
    scope: (0, pg_core_1.text)(),
    password: (0, pg_core_1.text)(), // scrypt hash written by Better Auth; never selected by application code
    createdAt: (0, pg_core_1.timestamp)({ withTimezone: true }).notNull().defaultNow(),
    updatedAt: (0, pg_core_1.timestamp)({ withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    (0, pg_core_1.uniqueIndex)("account_provider_account_uidx").on(t.providerId, t.accountId),
    (0, pg_core_1.index)("account_user_idx").on(t.userId),
]);
exports.verification = (0, pg_core_1.pgTable)("verification", {
    id: (0, pg_core_1.uuid)()
        .primaryKey()
        .default((0, drizzle_orm_1.sql) `gen_random_uuid()`),
    identifier: (0, pg_core_1.text)().notNull(),
    value: (0, pg_core_1.text)().notNull(),
    expiresAt: (0, pg_core_1.timestamp)({ withTimezone: true }).notNull(),
    createdAt: (0, pg_core_1.timestamp)({ withTimezone: true }).notNull().defaultNow(),
    updatedAt: (0, pg_core_1.timestamp)({ withTimezone: true }).notNull().defaultNow(),
}, (t) => [(0, pg_core_1.index)("verification_identifier_idx").on(t.identifier)]);
exports.twoFactor = (0, pg_core_1.pgTable)("two_factor", {
    id: (0, pg_core_1.uuid)()
        .primaryKey()
        .default((0, drizzle_orm_1.sql) `gen_random_uuid()`),
    secret: (0, pg_core_1.text)().notNull(),
    backupCodes: (0, pg_core_1.text)().notNull(),
    userId: (0, pg_core_1.uuid)()
        .notNull()
        .references(() => exports.user.id, { onDelete: "cascade" }),
    // Lockout state the twoFactor plugin reads and writes (two-factor/schema.mjs,
    // verify-two-factor.mjs:132-146). Defaults match the plugin's own defaults. (D-025)
    verified: (0, pg_core_1.boolean)().notNull().default(true),
    failedVerificationCount: (0, pg_core_1.integer)().notNull().default(0),
    lockedUntil: (0, pg_core_1.timestamp)({ withTimezone: true }),
}, (t) => [(0, pg_core_1.index)("two_factor_secret_idx").on(t.secret), (0, pg_core_1.index)("two_factor_user_idx").on(t.userId)]);
/** Better Auth `rateLimit.storage: "database"` (T04): survives restarts and multiple replicas. */
exports.rateLimit = (0, pg_core_1.pgTable)("rate_limit", {
    id: (0, pg_core_1.uuid)()
        .primaryKey()
        .default((0, drizzle_orm_1.sql) `gen_random_uuid()`),
    key: (0, pg_core_1.text)().notNull(),
    count: (0, pg_core_1.integer)().notNull(),
    lastRequest: (0, pg_core_1.bigint)({ mode: "number" }).notNull(),
});
// ---------- HIPAA audit log (T12 §9.2; immutable + hash-chained by drizzle/0000_audit.sql) ----------
// Action vocabulary follows ASTM E2147-18 §7.1.7 as referenced by 45 CFR 170.210(e). Extend with a migration.
exports.auditAction = (0, pg_core_1.pgEnum)("audit_action", [
    "read",
    "create",
    "update",
    "delete",
    "export",
    "print",
    "query",
    "login",
    "login_failed",
    "logout",
    "session_expired",
    "mfa_enrolled",
    "mfa_failed",
    "password_changed",
    "role_changed",
    "permission_changed",
    "access_denied",
    "break_glass_granted",
    "break_glass_used",
    "break_glass_revoked",
    "audit_status_changed",
    "encryption_status_changed",
    // J-246: a "Forgot password?" request; its reset is `password_changed` with reason `password_reset`.
    "password_reset_requested",
]);
exports.auditOutcome = (0, pg_core_1.pgEnum)("audit_outcome", ["success", "denied", "error"]);
exports.auditEvents = (0, pg_core_1.pgTable)("audit_events", {
    id: (0, pg_core_1.bigint)({ mode: "bigint" }).primaryKey().generatedAlwaysAsIdentity(),
    // §7.1.1 date/time — DB clock is the time source.
    occurredAt: (0, pg_core_1.timestamp)({ withTimezone: true }).notNull().defaultNow(),
    // §7.1.6 user identification
    actorUserId: (0, pg_core_1.uuid)(), // null only for unauthenticated failures
    actorRole: (0, pg_core_1.text)(),
    actorSessionId: (0, pg_core_1.text)(),
    onBehalfOf: (0, pg_core_1.uuid)(), // admin acting as / break-glass grantor
    // §7.1.7 action type and outcome
    action: (0, exports.auditAction)().notNull(),
    outcome: (0, exports.auditOutcome)().notNull(),
    // §7.1.2 patient identification and §7.1.8 record identifier. text, not uuid: a patient id from
    // another system may be any opaque string, and this column must accept any patient identifier
    // this system audits, not only UUID actors.
    patientId: (0, pg_core_1.text)(),
    resourceType: (0, pg_core_1.text)().notNull(), // e.g. "encounter", "user", "session"
    resourceId: (0, pg_core_1.text)(),
    // where
    requestId: (0, pg_core_1.text)(), // Fastify request.id
    traceId: (0, pg_core_1.text)(), // OpenTelemetry trace id
    ipAddress: (0, pg_core_1.inet)(),
    userAgent: (0, pg_core_1.text)(),
    route: (0, pg_core_1.text)(), // method + route pattern, never the raw URL
    // §7.1.9 details — field NAMES and counts only. PHI values are forbidden here.
    details: (0, pg_core_1.jsonb)().$type().notNull().default({}),
    // tamper evidence (45 CFR 170.210(c) SHA-2); set by the audit_events_chain trigger
    prevHash: (0, pg_core_1.text)(),
    rowHash: (0, pg_core_1.text)().notNull().default(""),
}, (t) => [
    (0, pg_core_1.index)("audit_events_patient_idx").on(t.patientId, t.occurredAt),
    (0, pg_core_1.index)("audit_events_actor_idx").on(t.actorUserId, t.occurredAt),
    (0, pg_core_1.index)("audit_events_occurred_idx").on(t.occurredAt),
]);
// ---------- Zod schemas derived from the tables (drizzle-zod 0.8.3) ----------
/** Public shape of a user account for the admin users route; the password hash lives in `account`, never here. */
exports.userPublicSchema = (0, drizzle_zod_1.createSelectSchema)(exports.user, {
    email: (s) => s.email(),
})
    .pick({
    id: true,
    email: true,
    name: true,
    role: true,
    banned: true,
    twoFactorEnabled: true,
    createdAt: true,
})
    .meta({ id: "User", description: "A staff user (not a patient)" });
//# sourceMappingURL=schema.js.map